import * as z from "zod";
import { EasyPostCarrierType } from "./carrier-account";

/** Per-account price card for carriers EasyPost quotes at a placeholder price. */
export const ReturnRateCard = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** ISO2 country the card ships into: the merchant's hub. */
  destinationCountry: z.string().regex(/^[A-Z]{2}$/),
  /** ISO2 shopper country → decimal price string. */
  pricesByOriginCountry: z.record(
    z.string().regex(/^[A-Z]{2}$/),
    z.string().regex(/^\d+(\.\d{1,2})?$/),
  ),
});
export type ReturnRateCard = z.infer<typeof ReturnRateCard>;

export interface ReturnRateCardPrice {
  price: string;
  currency: string;
}

/** DPD NL quotes every service at 0.01 and invoices postage itself. */
export const requiresReturnRateCard = (
  easypostCarrierType: EasyPostCarrierType,
): boolean => easypostCarrierType === EasyPostCarrierType.DPD_NL;

/** Mongoose hydrates the price map as a Map; the raw document has an object. */
export function returnRateCardFromStored(
  stored:
    | {
        currency: string;
        destinationCountry: string;
        pricesByOriginCountry: Map<string, unknown> | Record<string, unknown>;
      }
    | null
    | undefined,
): ReturnRateCard | undefined {
  if (!stored) {
    return undefined;
  }
  const entries =
    stored.pricesByOriginCountry instanceof Map
      ? [...stored.pricesByOriginCountry.entries()]
      : Object.entries(stored.pricesByOriginCountry);
  const parsed = ReturnRateCard.safeParse({
    currency: stored.currency,
    destinationCountry: stored.destinationCountry,
    pricesByOriginCountry: Object.fromEntries(entries),
  });
  return parsed.success ? parsed.data : undefined;
}

/** Card price for the physical lane; undefined when the lane is off the card. */
export function returnRateCardPrice(
  card: ReturnRateCard,
  {
    originCountry,
    destinationCountry,
  }: {
    originCountry: string | null | undefined;
    destinationCountry: string | null | undefined;
  },
): ReturnRateCardPrice | undefined {
  if (
    !originCountry ||
    destinationCountry?.toUpperCase() !== card.destinationCountry.toUpperCase()
  ) {
    return undefined;
  }
  const price = card.pricesByOriginCountry[originCountry.toUpperCase()];
  return price ? { price, currency: card.currency } : undefined;
}
